import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Alert,
  TextField,
  InputAdornment,
  CircularProgress,
} from '@mui/material';
import Phone from '@mui/icons-material/Phone';
import StorefrontIcon from '@mui/icons-material/Storefront';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import ArrowBack from '@mui/icons-material/ArrowBack';
import { findWorkerByPhone } from '../../mock/workers';
import { Worker } from '../../types';

interface WorkerLoginPageProps {
  onLoginSuccess: (worker: Worker) => void;
  onBackToMain: () => void;
}

export const WorkerLoginPage: React.FC<WorkerLoginPageProps> = ({
  onLoginSuccess,
  onBackToMain,
}) => {
  const [step, setStep] = useState<'phone' | 'code'>('phone');
  const [phone, setPhone] = useState('');
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [foundWorker, setFoundWorker] = useState<Worker | null>(null);
  const [countdown, setCountdown] = useState(0);

  const codeInputRefs = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  const formatPhoneNumber = (value: string) => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 3) return numbers;
    if (numbers.length <= 6) return `(${numbers.slice(0, 3)}) ${numbers.slice(3)}`;
    return `(${numbers.slice(0, 3)}) ${numbers.slice(3, 6)}-${numbers.slice(6, 10)}`;
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatPhoneNumber(e.target.value);
    setPhone(formatted);
    setError(null);
  };

  const handleSendCode = async () => {
    const digits = phone.replace(/\D/g, '');
    if (digits.length < 10) {
      setError('Please enter a valid phone number');
      return;
    }

    setLoading(true);
    setError(null);

    await new Promise(resolve => setTimeout(resolve, 1000));

    const worker = findWorkerByPhone(phone);
    if (worker) {
      setFoundWorker(worker);
      setStep('code');
      setCountdown(30);
    } else {
      setError('Phone number not found. Try: (555) 123-4567 for demo.');
    }

    setLoading(false);
  };

  const handleCodeChange = (index: number, value: string) => {
    if (!/^\d*$/.test(value)) return;

    const newCode = [...code];
    newCode[index] = value.slice(-1);
    setCode(newCode);
    setError(null);

    if (value && index < 5) {
      codeInputRefs.current[index + 1]?.focus();
    }
  };

  const handleCodeKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      codeInputRefs.current[index - 1]?.focus();
    }
  };

  const handleCodePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    const newCode = [...code];
    for (let i = 0; i < pasted.length; i++) {
      newCode[i] = pasted[i];
    }
    setCode(newCode);
    if (pasted.length === 6) {
      codeInputRefs.current[5]?.focus();
    }
  };

  const handleVerifyCode = async () => {
    const fullCode = code.join('');
    if (fullCode.length !== 6) {
      setError('Please enter the 6-digit code');
      return;
    }

    setLoading(true);
    setError(null);

    await new Promise(resolve => setTimeout(resolve, 1000));

    if (fullCode === '123456' && foundWorker) {
      onLoginSuccess(foundWorker);
    } else {
      setError('Invalid verification code. Try 123456 for demo.');
    }

    setLoading(false);
  };

  const handleResendCode = async () => {
    if (countdown > 0) return;

    setLoading(true);
    await new Promise(resolve => setTimeout(resolve, 500));
    setCountdown(30);
    setLoading(false);
  };

  const handleBack = () => {
    if (step === 'code') {
      setStep('phone');
      setCode(['', '', '', '', '', '']);
      setFoundWorker(null);
      setError(null);
    } else {
      onBackToMain();
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
        p: 2,
      }}
    >
      <Paper
        sx={{
          p: 4,
          maxWidth: 420,
          width: '100%',
          textAlign: 'center',
          borderRadius: 3,
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mb: 3 }}>
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: 2,
              bgcolor: '#f59e0b',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <StorefrontIcon sx={{ fontSize: 32, color: 'white' }} />
          </Box>
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: 2,
              bgcolor: '#3b82f6',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <ConfirmationNumberIcon sx={{ fontSize: 32, color: 'white' }} />
          </Box>
        </Box>

        <Typography variant="h5" fontWeight={700} gutterBottom>
          {step === 'phone' ? 'Worker Sign In' : 'Enter Verification Code'}
        </Typography>

        {step === 'phone' ? (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
              Enter your phone number to receive a verification code
            </Typography>

            <TextField
              fullWidth
              label="Phone Number"
              value={phone}
              onChange={handlePhoneChange}
              placeholder="(555) 123-4567"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Phone />
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 2 }}
              disabled={loading}
            />

            {error && (
              <Alert severity="error" sx={{ mb: 2, textAlign: 'left' }}>
                {error}
              </Alert>
            )}

            <Button
              variant="contained"
              fullWidth
              size="large"
              onClick={handleSendCode}
              disabled={loading || phone.replace(/\D/g, '').length < 10}
              sx={{
                height: 56,
                fontSize: '1.1rem',
                fontWeight: 600,
                borderRadius: 2,
                bgcolor: '#059669',
                '&:hover': { bgcolor: '#047857' },
                '&:disabled': { bgcolor: '#d1d5db' },
              }}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : 'Send Verification Code'}
            </Button>
          </>
        ) : (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              We sent a code to
            </Typography>
            <Typography variant="body1" fontWeight={600} sx={{ mb: 3 }}>
              {phone}
            </Typography>

            {foundWorker && (
              <Box
                sx={{
                  p: 2,
                  mb: 3,
                  bgcolor: '#f0fdf4',
                  borderRadius: 2,
                  border: '1px solid #bbf7d0',
                }}
              >
                <Typography variant="body2" color="success.dark" fontWeight={600}>
                  Welcome back, {foundWorker.firstName}!
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {foundWorker.workerTypes.map(t => t === 'concession' ? 'Concession' : 'Ticket').join(' & ')} Worker
                </Typography>
              </Box>
            )}

            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                gap: 1,
                mb: 3,
              }}
              onPaste={handleCodePaste}
            >
              {code.map((digit, index) => (
                <TextField
                  key={index}
                  inputRef={(el) => (codeInputRefs.current[index] = el)}
                  value={digit}
                  onChange={(e) => handleCodeChange(index, e.target.value)}
                  onKeyDown={(e) => handleCodeKeyDown(index, e)}
                  inputProps={{
                    maxLength: 1,
                    style: {
                      textAlign: 'center',
                      fontSize: '1.5rem',
                      fontWeight: 700,
                      padding: '12px 0',
                    },
                  }}
                  sx={{
                    width: 48,
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    },
                  }}
                  disabled={loading}
                />
              ))}
            </Box>

            {error && (
              <Alert severity="error" sx={{ mb: 2, textAlign: 'left' }}>
                {error}
              </Alert>
            )}

            <Button
              variant="contained"
              fullWidth
              size="large"
              onClick={handleVerifyCode}
              disabled={loading || code.some(d => !d)}
              sx={{
                height: 56,
                fontSize: '1.1rem',
                fontWeight: 600,
                borderRadius: 2,
                bgcolor: '#059669',
                '&:hover': { bgcolor: '#047857' },
                '&:disabled': { bgcolor: '#d1d5db' },
                mb: 2,
              }}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : 'Verify & Sign In'}
            </Button>

            <Button
              variant="text"
              onClick={handleResendCode}
              disabled={countdown > 0 || loading}
              sx={{ color: countdown > 0 ? 'text.secondary' : 'primary.main' }}
            >
              {countdown > 0 ? `Resend code in ${countdown}s` : 'Resend Code'}
            </Button>
          </>
        )}

        <Button
          variant="text"
          startIcon={<ArrowBack />}
          onClick={handleBack}
          sx={{ mt: 2, color: 'text.secondary' }}
        >
          {step === 'phone' ? 'Back to Main Login' : 'Change Phone Number'}
        </Button>

        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: 'block', mt: 3 }}
        >
          Demo: Phone (555) 123-4567, code 123456
        </Typography>
      </Paper>
    </Box>
  );
};
